import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell, { Breadcrumbs } from "@/components/Shell";
import { Card, Btn, Input, Select, Label, Spinner } from "@/components/ui-kit";
import api from "@/lib/api";
import { formatApiError, PROJECT_STATUSES, toISODate } from "@/lib/format";

const ProjectForm = ({ mode = "new" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: "",
    name: "",
    start_date: "",
    end_date: "",
    budget: "",
    status: "Not Started",
  });
  const [jobNumber, setJobNumber] = useState("");
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    if (mode === "edit") {
      api.get(`/projects/${id}`).then((r) => {
        setForm({
          client_id: r.data.client_id,
          name: r.data.name || "",
          start_date: r.data.start_date || "",
          end_date: r.data.end_date || "",
          budget: r.data.budget || "",
          status: r.data.status || "Not Started",
        });
        setJobNumber(r.data.job_number);
      }).finally(() => setLoading(false));
    }
  }, [id, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {
        ...form,
        start_date: toISODate(form.start_date),
        end_date: toISODate(form.end_date),
        budget: parseFloat(form.budget || 0),
      };
      if (mode === "new") {
        const { data } = await api.post("/projects", payload);
        navigate(`/projects/${data.id}`);
      } else {
        await api.put(`/projects/${id}`, payload);
        navigate(`/projects/${id}`);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Shell><div className="flex justify-center py-20"><Spinner /></div></Shell>;

  return (
    <Shell>
      <Breadcrumbs items={[{ to: "/projects", label: "Projects" }, { label: mode === "new" ? "New Project" : "Edit Project" }]} />
      <h1 className="font-display text-4xl font-bold text-zinc-950 mb-1">{mode === "new" ? "New Project" : "Edit Project"}</h1>
      <p className="text-sm text-zinc-500 mb-8">Project Details</p>

      <Card className="p-6 max-w-2xl">
        <form onSubmit={submit} className="space-y-4" data-testid="project-form">
          <div>
            <Label required>Client</Label>
            <Select required data-testid="project-client-select" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
              <option value="">Select client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
          <div>
            <Label required>Project Name</Label>
            <Input required data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          {mode === "edit" && (
            <div>
              <Label>Job Number</Label>
              <Input disabled value={jobNumber} data-testid="project-job-number" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start Date</Label>
              <Input type="date" data-testid="project-start-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <Label>End Date</Label>
              <Input type="date" data-testid="project-end-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Budget (£)</Label>
            <Input type="number" step="0.01" data-testid="project-budget-input" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
          </div>
          <div>
            <Label required>Status</Label>
            <Select data-testid="project-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {PROJECT_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </Select>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2 pt-2">
            <Btn type="submit" disabled={saving} data-testid="project-save-btn">{mode === "new" ? "Create Project" : "Update Project"}</Btn>
            <Btn type="button" variant="secondary" onClick={() => navigate(-1)} data-testid="project-cancel-btn">Cancel</Btn>
          </div>
        </form>
      </Card>
    </Shell>
  );
};

export default ProjectForm;
